import { cn } from "@/lib/utils";
import { useState, useEffect } from "react";
import axios from "axios";
import {
  HouseIcon,
  MessageSquareTextIcon,
  SparklesIcon,
  GlobeIcon,
  ClipboardListIcon,
  ChartLineIcon,
  TrendingUpIcon,
  CodeIcon,
  LibraryBigIcon,
  Users2Icon,
  SettingsIcon,
  ChevronsUpDown,
} from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

interface Site {
  siteUrl: string;
  permissionLevel: string;
}

interface ProductPreviewProps {
  brandName?: string;
  domain?: string;
  className?: string;
  sites?: Site[];
  onSiteChange?: (siteUrl: string) => void;
  onAddDomain?: () => void;
  selectedTopics?: string[];
  showTopicsHeader?: boolean;
  hideSidebar?: boolean;
}

import { API_BASE_URL } from "@/config";

// ... imports

// ... interface definitions

export function ProductPreview({
  brandName,
  domain,
  className,
  sites = [],
  onSiteChange,
  onAddDomain,
  selectedTopics = [],
  showTopicsHeader = false,
  hideSidebar = false,
}: ProductPreviewProps) {
  const [open, setOpen] = useState(false);
  const [siteMetadata, setSiteMetadata] = useState<Record<string, { siteName?: string }>>({});

  // Fetch metadata for all sites to display brand names in the popover
  useEffect(() => {
    const fetchAllMetadata = async () => {
      if (sites.length === 0) return;
      
      try {
        // Extract URLs
        const urls = sites.map(site => site.siteUrl);
        
        // Batch fetch metadata (Single API call instead of N calls)
        const response = await axios.post(`${API_BASE_URL}/api/batch-site-metadata`, {
          urls
        });
        
        const results = response.data.results || {};
        const metadata: Record<string, { siteName?: string }> = {};
        
        // Process results
        Object.entries(results).forEach(([url, data]: [string, any]) => {
          if (data && data.site_name) {
            metadata[url] = { siteName: data.site_name };
          }
        });

        setSiteMetadata(metadata);
      } catch (error) {
        console.error("Failed to batch fetch metadata", error);
      }
    };

    if (sites.length > 0) {
      fetchAllMetadata();
    }
  }, [sites]);

  // If brandName is provided, use it.
  // If not, fallback to domain (cleaned).
  // Clean site URLs for display
  const cleanSiteUrl = (url: string) => {
    let cleaned = url.replace(/^https?:\/\//, "");
    cleaned = cleaned.replace(/^www\./, "");
    return cleaned.replace(/\/$/, "");
  };

  // If neither, fallback to "Acme".
  const displayName =
    brandName ||
    (domain ? cleanSiteUrl(domain) : "Acme");

  // Simple domain validation
  const isValidDomain = (domain: string) => {
    const cleaned = cleanSiteUrl(domain);
    const domainRegex = /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9]?\.[a-zA-Z]{2,}$/;
    return domainRegex.test(cleaned);
  };

  const faviconUrl = domain && isValidDomain(domain)
    ? `https://www.google.com/s2/favicons?domain=${cleanSiteUrl(domain)}&sz=64`
    : null;



  return (
    <div
      className={cn(
        "relative w-full overflow-hidden rounded-tl-xl rounded-tr-xl border border-slate-200 bg-slate-50 shadow-2xl",
        className
      )}
    >
      <div className="flex h-full">
        {/* Sidebar - Fixed width */}
        {!hideSidebar && (
          <div className="w-64 flex-shrink-0 border-r border-slate-200 bg-white flex flex-col">
            {/* Brand Header */}
            <div className="mb-6 px-4 pt-4">
              <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild disabled={sites.length <= 1}>
                  <button
                    role="combobox"
                    aria-expanded={open}
                    className={cn(
                      "flex w-full items-center gap-2 rounded-md px-2 py-2 font-semibold transition-colors",
                      sites.length > 1 &&
                        "hover:bg-slate-100 hover:ring-1 hover:ring-slate-200",
                      open && "bg-slate-100 ring-1 ring-slate-200"
                    )}
                  >
                    <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md border bg-white">
                      {faviconUrl ? (
                        <img
                          src={faviconUrl}
                          alt={displayName}
                          className="h-4 w-4 object-contain"
                        />
                      ) : (
                        <span className="text-xs font-bold text-slate-700">
                          {displayName.charAt(0).toUpperCase()}
                        </span>
                      )}
                    </div>
                    <span className="font-semibold text-slate-900 truncate min-w-0 flex-1 text-left">
                      {displayName}
                    </span>
                    {sites.length > 1 && (
                      <ChevronsUpDown className="h-4 w-4 flex-shrink-0 opacity-50" />
                    )}
                  </button>
                </PopoverTrigger>

                {sites.length > 1 && (
                  <PopoverContent align="start" className="w-[240px] p-0">
                    <div className="max-h-[300px] overflow-y-auto">
                      {sites.map((site) => {
                        const cleanedUrl = cleanSiteUrl(site.siteUrl);
                        const metadata = siteMetadata[site.siteUrl];
                        const displayName = metadata?.siteName || cleanedUrl;
                        const isSelected = site.siteUrl === domain;

                        return (
                          <button
                            key={site.siteUrl}
                            onClick={() => {
                              onSiteChange?.(site.siteUrl);
                              setOpen(false);
                            }}
                            className={cn(
                              "flex w-full items-center gap-2 px-3 py-2 text-sm transition-colors hover:bg-slate-100",
                              isSelected && "bg-slate-50 font-semibold"
                            )}
                          >
                            <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded border bg-white">
                              <img
                                src={`https://www.google.com/s2/favicons?domain=${site.siteUrl}&sz=64`}
                                alt={cleanedUrl}
                                className="h-3 w-3 object-contain"
                                onError={(e) => {
                                  const target = e.target as HTMLImageElement;
                                  target.style.display = "none";
                                  const parent = target.parentElement;
                                  if (parent) {
                                    parent.innerHTML = `<span class="text-xs font-bold text-slate-700">${cleanedUrl
                                      .charAt(0)
                                      .toUpperCase()}</span>`;
                                  }
                                }}
                              />
                            </div>
                            <span className="truncate flex-1 text-left">
                              {displayName}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </PopoverContent>
                )}

                {/* Manual mode - show Add Domain when no domain */}
                {sites.length === 0 && !domain && onAddDomain && (
                  <PopoverContent align="start" className="w-[240px] p-3">
                    <button
                      onClick={() => {
                        onAddDomain();
                        setOpen(false);
                      }}
                      className="flex w-full items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-medium text-slate-900 bg-slate-100 hover:bg-slate-200 transition-colors"
                    >
                      <svg
                        className="h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M12 4v16m8-8H4"
                        />
                      </svg>
                      Add Domain
                    </button>
                  </PopoverContent>
                )}
              </Popover>
            </div>

            {/* Navigation */}
            <div className="space-y-1 px-6 overflow-y-auto no-scrollbar">
              <NavItem icon={HouseIcon} skeleton />
              <NavItem icon={MessageSquareTextIcon} label="Prompts" active />
              <NavItem icon={SparklesIcon} skeleton />
              <NavItem icon={GlobeIcon} skeleton />
              <NavItem icon={ClipboardListIcon} skeleton />
              <NavItem icon={ChartLineIcon} skeleton />
              <NavItem icon={TrendingUpIcon} skeleton />
              <NavItem icon={CodeIcon} skeleton />
              <NavItem icon={LibraryBigIcon} skeleton />
              <NavItem icon={Users2Icon} skeleton />
            </div>
            <div className="space-y-1 px-6 overflow-y-auto no-scrollbar pt-6">
              <div className="h-2 w-20 rounded bg-slate-200 mb-2" />
              <NavItem icon={SettingsIcon} skeleton />
            </div>

            <div className="mt-auto pt-4 border-t border-slate-200 px-2 space-y-3">
              <div className="flex items-center gap-2 pt-1 px-4 pb-2">
                <div className="h-7 w-7 rounded-full bg-slate-200" />
                <div className="flex-1 space-y-1">
                  <div className="h-3 w-28 rounded bg-slate-200" />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="flex-1 p-6 flex flex-col overflow-hidden bg-slate-50">
          {/* Header */}
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-lg font-semibold text-slate-950">Prompts</h2>
          </div>
          {/* Stats Cards */}
          <div className="grid grid-cols-3 mb-8 shadow-sm w-full">
            <div className="rounded-none rounded-tl-md rounded-bl-md border-l border-t border-b border-slate-200 p-4 bg-white w-full">
              <div className="h-3 w-16 rounded bg-slate-50" />
              <div className="flex items-center justify-between pt-1">
                <div className="h-4 w-8 rounded bg-slate-100" />
              </div>
            </div>
            <div className="rounded-none border border-slate-200 p-4 bg-white w-full">
              <div className="h-3 w-16 rounded bg-slate-50" />
              <div className="flex items-center justify-between pt-1">
                <div className="h-4 w-8 rounded bg-slate-100" />
              </div>
            </div>
            <div className="rounded-none rounded-tr-md rounded-br-md border-r border-t border-b border-slate-200 p-4 bg-white w-full">
              <div className="h-3 w-16 rounded bg-slate-50" />
              <div className="flex items-center justify-between pt-1">
                <div className="h-4 w-8 rounded bg-slate-100" />
              </div>
            </div>
          </div>

          {/* Table Skeleton (Mock) */}
          <div className="flex-1 min-h-0 rounded-md border border-slate-200 bg-white overflow-hidden flex flex-col shadow-sm">
            <div className="grid grid-cols-12 gap-4 border-b border-slate-200 bg-slate-50 px-4 py-3">
              <div className="col-span-4 relative h-3">
                <div 
                  className={cn(
                    "absolute inset-0 h-3 w-20 rounded bg-slate-200 transition-opacity duration-700",
                    showTopicsHeader ? "opacity-0" : "opacity-100"
                  )} 
                />
                <span 
                  className={cn(
                    "absolute inset-0 text-xs tracking-wider font-semibold text-slate-800 transition-opacity duration-700 flex items-center",
                    showTopicsHeader ? "opacity-100" : "opacity-0"
                  )}
                >
                  Stages
                </span>
              </div>
              <div className="col-span-2"><div className="h-3 w-10 rounded bg-slate-200" /></div>
              <div className="col-span-2"><div className="h-3 w-14 rounded bg-slate-200" /></div>
              <div className="col-span-2"><div className="h-3 w-14 rounded bg-slate-200" /></div>
              <div className="col-span-2"><div className="h-3 w-20 rounded bg-slate-200" /></div>
            </div>
            <div className="flex-1 overflow-hidden">
              {[...Array(7)].map((_, i) => {
                const stageId = selectedTopics[i];
                const stageLabel = stageId ? {
                  "awareness": "Awareness",
                  "evaluation": "Evaluation",
                  "decision": "Decision",
                  "retention": "Retention",
                  "advocacy": "Advocacy"
                }[stageId] : null;

                return (
                  <div key={i} className="grid grid-cols-12 gap-4 items-center border-b border-slate-100 px-4 py-3 last:border-0">
                    <div className="col-span-4 space-y-2">
                      {stageLabel ? (
                        <div className="text-sm font-medium text-slate-950">{stageLabel}</div>
                      ) : (
                        <>
                          <div className="h-3 w-3/4 rounded-md bg-slate-100" />
                          <div className="h-3 w-1/2 rounded-md bg-slate-100" />
                        </>
                      )}
                    </div>
                    <div className="col-span-2 space-y-2">
                      <div className="h-3 w-16 rounded-md bg-slate-100" />
                      <div className="h-3 w-12 rounded-md bg-slate-100" />
                    </div>
                    <div className="col-span-2 flex items-center gap-2">
                      <div className="h-2 w-2 rounded-md bg-red-100" />
                      <div className="h-3 w-12 rounded-md bg-slate-100" />
                    </div>
                    <div className="col-span-2 flex items-center gap-2">
                      <div className="h-2 w-2 rounded-md bg-red-100" />
                      <div className="h-3 w-8 rounded-md bg-slate-100" />
                    </div>
                    <div className="col-span-2 flex gap-1 flex-wrap">
                      <div className="h-3 w-16 rounded-md bg-slate-100" />
                      <div className="h-3 w-10 rounded-md bg-slate-100" />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function NavItem({
  icon: Icon,
  label,
  active,
  skeleton,
}: {
  icon: any;
  label?: string;
  active?: boolean;
  skeleton?: boolean;
}) {
  return (
    <div
      className={cn(
        "flex items-center gap-3 rounded-md px-2 py-2 text-sm font-medium transition-colors",
        active ? "bg-slate-50 text-slate-950" : "text-slate-400"
      )}
    >
      <Icon
        className={cn("h-4 w-4", active ? "text-slate-950" : "text-slate-400")}
      />
      {skeleton ? (
        <div className="h-3 w-24 rounded bg-slate-200/60" />
      ) : (
        <span className="flex-1">{label}</span>
      )}
    </div>
  );
}

